import React, { useEffect, useRef } from 'react';
import { MessageSquare, Users, Bot, CheckCircle } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Registrar o plugin ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

interface SolutionCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefit: string;
}

const Problems: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = React.useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animação do título principal
      gsap.fromTo(titleRef.current,
        { 
          opacity: 0, 
          y: 30,
          scale: 0.95
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Animação do subtítulo
      gsap.fromTo(subtitleRef.current,
        { 
          opacity: 0, 
          y: 20
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power2.out",
          delay: 0.2,
          scrollTrigger: {
            trigger: subtitleRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Animação dos cards com stagger suave
      const cards = cardsRef.current?.children;
      if (cards) {
        gsap.fromTo(Array.from(cards),
          { 
            opacity: 0, 
            y: 40,
            scale: 0.9
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            ease: "power2.out",
            stagger: 0.1,
            scrollTrigger: {
              trigger: cardsRef.current,
              start: "top 80%",
              toggleActions: "play none none reverse"
            }
          }
        );
      }

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const solutions: SolutionCard[] = [
    {
      icon: <MessageSquare className="w-8 h-8 text-white" />,
      title: "Adeus Discagem Lenta",
      description: "Esqueça a digitação manual. Com a extensão Onflow, você liga com um clique direto do navegador.",
      benefit: "Mais chamadas por hora"
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-white" />,
      title: "Visibilidade Total",
      description: "Saiba exatamente o que acontece nas ligações com monitoria em tempo real e relatórios precisos.",
      benefit: "Fim da gestão por feeling"
    },
    {
      icon: <Users className="w-8 h-8 text-white" />,
      title: "Auditoria com IA",
      description: "IA que transcreve e analisa cada chamada, identificando objeções e oportunidades automaticamente.",
      benefit: "Feedback 10x mais rápido"
    },
    {
      icon: <Bot className="w-8 h-8 text-white" />,
      title: "Operação Confiável",
      description: "BINA inteligente que melhora a entregabilidade e garante que suas chamadas cheguem ao destino.",
      benefit: "Maior taxa de conversão"
    }
  ];

  return (
    <section
      id="problems"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="pt-16 lg:pt-20 pb-16 lg:pb-20 relative overflow-hidden group/problems"
      style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' }}
    >
      {/* Elementos Decorativos - Grid Linear Branco com Hover Suave */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Grid Base - Mais visível quando apagado */}
        <div className="absolute inset-0 opacity-[0.05]" 
          style={{ 
            backgroundImage: `
              linear-gradient(to right, white 1px, transparent 1px),
              linear-gradient(to bottom, white 1px, transparent 1px)
            `, 
            backgroundSize: '40px 40px' 
          }} 
        />
        
        {/* Grid Hover - Mais suave e com mais blur */}
        <div className="absolute inset-0 opacity-0 group-hover/problems:opacity-[0.15] transition-opacity duration-700" 
          style={{ 
            backgroundImage: `
              linear-gradient(to right, white 1px, transparent 1px),
              linear-gradient(to bottom, white 1px, transparent 1px)
            `, 
            backgroundSize: '40px 40px',
            WebkitMaskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
            maskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
          }} 
        />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 
            ref={titleRef}
            className="text-2xl lg:text-4xl font-bold text-white mb-4"
          >
            Por que as empresas confiam no Onflow?
          </h2>
          <p 
            ref={subtitleRef}
            className="text-base text-blue-100 max-w-2xl mx-auto"
          >
            Escale sua operação de voz com previsibilidade, controle e inteligência
          </p>
        </div>

          {/* Solutions Grid */}
        <div 
          ref={cardsRef}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12"
        >
          {solutions.map((solution, index) => (
            <div
              key={index}
              className="group bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white/25 transition-all duration-300 hover:-translate-y-1 flex flex-col items-center text-center lg:items-start lg:text-left"
            >
              <div className="mb-4 p-3 bg-white/25 rounded-xl w-fit">
                {solution.icon}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {solution.title}
              </h3>
              <p className="text-blue-100 text-sm mb-3 leading-relaxed">
                {solution.description}
              </p>
              <div className="inline-flex items-center text-xs font-medium text-white bg-white/25 px-3 py-1 rounded-full">
                <CheckCircle className="w-3 h-3 mr-1" />
                {solution.benefit}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Problems;
